import { useEffect, useState, type FormEvent } from 'react';
import type { BillingStatus, UserSummary } from '@blackblack/shared';
import { api, errorMessage } from '../../lib/api';
import { invalidate, setQueryData, useQuery } from '../../lib/query';
import { Link, navigate, useSearchParams } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { useToast } from '../../state/toast';
import { Icon } from '../../components/ui/Icon';
import { Avatar, Button, EmptyState, ErrorState, Skeleton, TextField } from '../../components/ui/primitives';
import { Page, PageHeader } from '../../components/layout';
import { euro, goToStripe } from './money';
import styles from './billing.module.css';

const KEY = 'billing:me';

/** Runs a POST that returns a Stripe URL and sends the browser there. */
function useStripeRedirect() {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const go = async (path: string) => {
    setBusy(path);
    try {
      goToStripe((await api.post<{ url: string }>(path)).url);
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
      setBusy(null);
    }
  };
  return { busy, go };
}

export function BillingPage() {
  const q = useQuery<BillingStatus>(KEY, () => api.get('/billing/me'), { staleMs: 0 });
  const params = useSearchParams();
  const toast = useToast();
  const { refreshMe } = useAuth();

  // Back from Stripe: confirm, refresh and clean the URL.
  useEffect(() => {
    const checkout = params.get('checkout');
    const connect = params.get('connect');
    if (!checkout && !connect) return;
    if (checkout === 'pro') toast('Welkom bij Creator Pro! Het kan een paar seconden duren voor alles actief is.', { tone: 'success' });
    if (connect === 'return') toast('Gegevens ontvangen. Stripe controleert ze, meestal binnen een paar minuten.');
    // Not cleared on re-run: cleaning the URL below changes `params` immediately.
    setTimeout(() => {
      void q.refetch();
      void refreshMe();
      invalidate('profile:');
    }, 2500); // the webhook usually lands within a second or two
    navigate('/settings/billing', { replace: true });
  }, [params]);

  const s = q.data;
  return (
    <Page>
      <PageHeader title="Inkomsten & Pro" fallback="/settings" />
      {q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : null}
      {!s && !q.error && (
        <div className={styles.card}>
          <Skeleton h={22} w={180} />
          <Skeleton h={60} />
        </div>
      )}
      {s && !s.enabled && <EmptyState icon="sparkle" title="Binnenkort beschikbaar" text="Betalingen zijn nog niet ingeschakeld op BlackBlack." />}
      {s?.enabled && (
        <>
          <ProCard status={s} />
          <CreatorCard status={s} onChange={(next) => setQueryData<BillingStatus>(KEY, () => next)} />
          {s.supporting.length > 0 && <SupportingCard supporting={s.supporting} />}
        </>
      )}
    </Page>
  );
}

function ProCard({ status: s }: { status: BillingStatus }) {
  const { busy, go } = useStripeRedirect();
  const until = s.pro.currentPeriodEnd ? new Date(s.pro.currentPeriodEnd).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
  return (
    <section className={`${styles.card} ${styles.proCard}`}>
      <h2 className={styles.cardTitle}>
        <Icon name="sparkle" size={20} /> Creator Pro {s.pro.active && <span className={styles.badge}>ACTIEF</span>}
      </h2>
      {s.pro.active ? (
        <>
          <p className={styles.muted}>
            {s.pro.status === 'past_due'
              ? 'Je laatste betaling is mislukt. Werk je betaalmethode bij om Pro te houden.'
              : until && (s.pro.cancelAtPeriodEnd ? `Je Pro-abonnement stopt op ${until}.` : `Wordt automatisch verlengd op ${until}.`)}
          </p>
          <Button variant="secondary" loading={busy === '/billing/portal'} onClick={() => void go('/billing/portal')}>
            Abonnement beheren
          </Button>
        </>
      ) : (
        <>
          <ul className={styles.perks}>
            <li><Icon name="check" size={16} /> 2× grotere video-uploads</li>
            <li><Icon name="check" size={16} /> Pro-badge op je profiel</li>
            <li><Icon name="check" size={16} /> Lagere platformfee op fan-steun: je houdt meer over</li>
          </ul>
          <Button block loading={busy === '/billing/pro/checkout'} onClick={() => void go('/billing/pro/checkout')}>
            Word Pro
          </Button>
          <p className={styles.muted}>Maandelijks opzegbaar. Je betaalt veilig via Stripe (iDEAL, kaart).</p>
        </>
      )}
    </section>
  );
}

function CreatorCard({ status: s, onChange }: { status: BillingStatus; onChange: (s: BillingStatus) => void }) {
  const { busy, go } = useStripeRedirect();
  const c = s.creator;
  return (
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>
        <Icon name="heart" size={20} /> Steun van fans
      </h2>
      {!c.connected && (
        <>
          <p className={styles.muted}>Laat fans je maandelijks steunen. Je stelt eenmalig je uitbetalingen in bij Stripe (identiteit en IBAN); daarna kies je je eigen bedrag.</p>
          <Button loading={busy === '/billing/connect/onboarding'} onClick={() => void go('/billing/connect/onboarding')}>
            Uitbetalingen instellen
          </Button>
        </>
      )}
      {c.connected && !c.chargesEnabled && (
        <>
          <p className={`${styles.muted} ${styles.pending}`}>
            {c.detailsSubmitted ? 'Stripe controleert je gegevens. Dit duurt meestal een paar minuten.' : 'Je instellingen bij Stripe zijn nog niet compleet.'}
          </p>
          <Button variant="secondary" loading={busy === '/billing/connect/onboarding'} onClick={() => void go('/billing/connect/onboarding')}>
            {c.detailsSubmitted ? 'Gegevens bekijken' : 'Verder met instellen'}
          </Button>
        </>
      )}
      {c.chargesEnabled && (
        <>
          <PriceForm current={c.supportPriceCents} onSaved={onChange} />
          <div className={styles.stat}>
            <span>Supporters</span>
            <strong>{c.supporterCount}</strong>
          </div>
          <div className={styles.stat}>
            <span>Platformfee</span>
            <strong>{c.feePercent}%</strong>
          </div>
          {c.supporterCount > 0 && <Supporters />}
          <Button variant="secondary" icon="chart" loading={busy === '/billing/connect/dashboard'} onClick={() => void go('/billing/connect/dashboard')}>
            Uitbetalingen bekijken
          </Button>
        </>
      )}
    </section>
  );
}

function PriceForm({ current, onSaved }: { current: number | null; onSaved: (s: BillingStatus) => void }) {
  const toast = useToast();
  const [value, setValue] = useState(current ? (current / 100).toFixed(2).replace('.', ',') : '4,99');
  const [busy, setBusy] = useState(false);

  const save = async (priceCents: number | null) => {
    setBusy(true);
    try {
      onSaved(await api.put<BillingStatus>('/billing/support-price', priceCents === null ? {} : { priceCents }));
      invalidate('profile:');
      toast(priceCents === null ? 'Fan-steun uitgezet' : `Fans kunnen je nu steunen voor ${euro(priceCents)} per maand`, { tone: 'success' });
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const cents = Math.round(Number(value.replace(',', '.')) * 100);
    if (!Number.isFinite(cents) || cents < 100 || cents > 10_000) return toast('Kies een bedrag tussen € 1 en € 100', { tone: 'error' });
    void save(cents);
  };

  return (
    <form onSubmit={submit} className={styles.row}>
      <TextField label="Bedrag per maand (€)" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value.replace(/[^\d.,]/g, ''))} hint={current ? `Nu: ${euro(current)} per maand` : 'Fan-steun staat nog uit'} />
      <Button type="submit" loading={busy}>
        {current ? 'Bijwerken' : 'Aanzetten'}
      </Button>
      {current !== null && (
        <Button type="button" variant="ghost" disabled={busy} onClick={() => void save(null)}>
          Uitzetten
        </Button>
      )}
    </form>
  );
}

function Supporters() {
  const q = useQuery<Array<{ user: UserSummary; since: string; amountCents: number }>>('billing:supporters', () => api.get('/billing/supporters'), { staleMs: 0 });
  if (!q.data?.length) return null;
  return (
    <ul className={styles.people}>
      {q.data.map((s) => (
        <li key={s.user.id}>
          <Avatar user={s.user} size={32} />
          <span>
            <Link to={`/u/${s.user.username}`}>@{s.user.username}</Link>
          </span>
          <small>{euro(s.amountCents)}/mnd</small>
        </li>
      ))}
    </ul>
  );
}

function SupportingCard({ supporting }: { supporting: BillingStatus['supporting'] }) {
  const { busy, go } = useStripeRedirect();
  return (
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>
        <Icon name="users" size={20} /> Jij steunt
      </h2>
      <ul className={styles.people}>
        {supporting.map((s) => (
          <li key={s.creator.id}>
            <Avatar user={s.creator} size={32} />
            <span>
              <Link to={`/u/${s.creator.username}`}>@{s.creator.username}</Link>
            </span>
            <small>{s.cancelAtPeriodEnd ? 'stopt binnenkort' : `${euro(s.amountCents)}/mnd`}</small>
          </li>
        ))}
      </ul>
      <Button variant="secondary" loading={busy === '/billing/portal'} onClick={() => void go('/billing/portal')}>
        Steun beheren of opzeggen
      </Button>
    </section>
  );
}
